import React, { useCallback, useEffect, useState } from 'react';
import { usePack, usePurchases, useResources } from '#src/@billing/use-billing.js';
import Button from '#lib/components/button.jsx';
import { Icon } from '@iconify-icon/react';
import { addClasses, cn } from '#lib/helpers/class.js';
import { LoadingIndicator } from '#lib/components/loading.jsx';
import useBilling from './use-billing.js';
import { StatusRingColor, findActivePackage, premiumPackages, getPurchasesRowsData } from './billing-helpers.js';
import { required } from '#lib/hooks/use-validator.js';
import toast from 'react-hot-toast';
import { useForm } from '#lib/hooks/use-form.js';
import useBlockNavigation from '#hooks/use-block-navigation.js';
import Input from '#lib/components/input.jsx';
import UnsavedChangesModal from '#components/unsaved-changes-modal.jsx';
import useBillingInfo from './use-billing-info.js';
import PackagesModal from './packages-modal.jsx';
import Combobox from '#lib/components/combobox.jsx';
import FloatingTooltip from '#components/tooltip.jsx';
import useCountries from '#hooks/use-countries.js';

function BillingInfo({ register, registerRadio, props, data, save, countries, currentCountry }) {
    const [filter, setFilter] = useState(currentCountry ? _t.countries(currentCountry) : '');
    const [options, setOptions] = useState(
        countries.filter(({ code }) => _t.countries(code).toLowerCase().startsWith(filter.toLowerCase()))
    );

    useEffect(() => {
        setOptions(countries.filter(({ code }) => _t.countries(code).toLowerCase().startsWith(filter.toLowerCase())));
    }, [filter]);
    return (
        <section className="w-full pt-2 pb-4 pr-2 mb-4 border-t border-neutral-200">
            <h2 className="mb-4 text-lg font-bold text-neutral-700">{_t.profile.billingInfo()}</h2>
            <form {...props.form} className="flex flex-col gap-4 mb-6">
                <div>
                    <label htmlFor="billingType" className="block mb-2 text-sm text-gray-700">
                        {_t.profile.accountType()}
                    </label>
                    <fieldset className="flex gap-4">
                        <div className="flex gap-1">
                            <input {...registerRadio('billingType', 'private')} />
                            <legend>{_t.profile.private()}</legend>
                        </div>
                        <div className="flex gap-1">
                            <input {...registerRadio('billingType', 'company')} />
                            <legend>{_t.profile.company()}</legend>
                        </div>
                    </fieldset>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 md:grid-rows-5 xl:grid-rows-2 xl:grid-cols-5 gap-y-4 md:gap-4">
                    <div className="grid grid-cols-1 col-span-1 gap-4 md:grid-cols-2 md:col-span-2">
                        {data?.billingType === 'private' ? (
                            <>
                                <Input.Full
                                    id="firstName"
                                    className="col-span-1"
                                    label={_t.profile.firstName()}
                                    {...register('firstName')}
                                    required
                                />

                                <Input.Full
                                    id="lastName"
                                    className="col-span-1"
                                    label={_t.profile.lastName()}
                                    {...register('lastName')}
                                    required
                                />
                            </>
                        ) : (
                            <>
                                <Input.Full
                                    id="companyName"
                                    className="col-span-1"
                                    label={_t.profile.companyName()}
                                    {...register('companyName')}
                                    required
                                />
                                <Input.Full
                                    id="vat"
                                    className="col-span-1"
                                    label={_t.profile.vatNumber()}
                                    {...register('vatNumber')}
                                    required
                                />
                            </>
                        )}
                    </div>
                    <div className="col-span-1">
                        <Input.Full id="email" label={_t.profile.email()} {...register('email')} required />
                    </div>
                    <div className="col-span-1">
                        <Input.Full id="tax" label={_t.profile.taxIdCode()} {...register('taxIdCode')} required />
                    </div>
                    <div className="col-span-1">
                        <Combobox.Full
                            name="country"
                            label={_t.profile.country()}
                            inPortal={false}
                            query={filter}
                            onQueryChange={(value) => setFilter(value)}
                            onChange={(value) => {
                                setFilter(_t.countries(value));
                            }}
                            options={options}
                            filterFn={() => true}
                            displayValue={(opt) => _t.countries(opt)}
                            makeReactKey={(opt) => opt.code}
                            accessValue={(opt) => opt.code}
                        />
                    </div>
                    <div className="col-span-1">
                        <Input.Full label={_t.profile.province()} {...register('province')} />
                    </div>
                    <div className="col-span-1">
                        <Input.Full label={_t.profile.city()} {...register('city')} />
                    </div>
                    <div className="col-span-1">
                        <Input.Full label={_t.profile.billingAddress()} {...register('billingAddress')} />
                    </div>
                    <div className="col-span-1">
                        <Input.Full label={_t.profile.streetNumber()} {...register('streetNumber')} />
                    </div>
                    <div className="col-span-1">
                        <Input.Full label={_t.profile.postalCode()} {...register('postalCode')} />
                    </div>
                </div>
            </form>
            <Button look="white" {...props.submit} busy={save.isLoading} className={addClasses('w-32 mt-0')}>
                <p className="font-bold">{save.isLoading ? _t.global.saveLoad() : _t.global.save()}</p>
            </Button>
        </section>
    );
}

export default function Billing() {
    const [errors, setErrors] = useState({});
    const { data: packs } = usePack();
    const { data: purchasesData, isLoading, isSuccess } = usePurchases();
    const { data: resourcesData, isLoading: isLoadingResources, isSuccess: isSuccessResources } = useResources();
    const { proceedNavigation, blocked, reset, unsavedChanges, setUnsavedChanges } = useBlockNavigation();
    const { save, ...profile } = useBillingInfo(setUnsavedChanges);

    const billingInfo = profile.data;

    const validation = {
        vatNumber: [required],
        taxIdCode: [required],
        country: [required],
        city: [required],
        postalCode: [required],
        billingAddress: [required]
    };

    const { register, registerRadio, handleChange, props, data } = useForm({
        validation,
        initialData: billingInfo,
        onSubmit: (data, _, event) => {
            event.preventDefault();
            /* if (!isValid({ phoneNumber: data.phoneNumber }, 'phoneNumber', 'phoneNumber')) {
                setErrors({ phoneNumber: { value: true } });
                return toast.error(_t.users.phoneNumberDescription());
            } */
            save.mutate(data);
        },
        setUnsavedChanges
    });

    const {
        data: countries,
        flags: { isLoading: isLoadingCountries, isSuccess: isSuccessCountries }
    } = useCountries();

    const { purchases, loading, isDialogOpen, setDialogOpen, stripePromise, currentPurchases } = useBilling({
        purchasesData,
        resourcesData
    });

    const handleBuyMore = useCallback(
        (e) => {
            const isBillingComplete =
                (data?.vatNumber || data?.taxIdCode) &&
                data?.country &&
                data?.city &&
                data?.postalCode &&
                data?.billingAddress;

            // this comes first!
            if (!isBillingComplete) {
                toast.error(_t.profile.completeBillingInfo());
                return;
            }
            if (unsavedChanges) {
                toast.error(_t.profile.saveBillingInfo());
                return;
            }
            setDialogOpen(true);
        },
        [data, unsavedChanges, setDialogOpen]
    );

    if (purchases) {
        const activePackage =
            Array.isArray(currentPurchases) && Array.isArray(packs) ? findActivePackage(currentPurchases, packs) : null;
        const purchasesRows = getPurchasesRowsData(purchases, activePackage);

        return (
            <React.Fragment>
                {(isLoading || isLoadingResources || isLoadingCountries) && (
                    <LoadingIndicator
                        className={addClasses('z-10 absolute top-0 left-0 w-full h-full justify-center items-center')}
                    />
                )}
                {isSuccess && isSuccessResources && isSuccessCountries && (
                    <section className="flex flex-col justify-between w-full pt-2 pb-4 pr-2">
                        <h2 className="mb-6 text-2xl font-bold text-neutral-700">{_t.sideboard.billing()}</h2>
                        <BillingInfo
                            key={`bi-${data?.billingType}`}
                            register={register}
                            registerRadio={registerRadio}
                            handleChange={handleChange}
                            props={props}
                            data={data}
                            save={save}
                            errors={errors}
                            countries={countries.countries}
                            currentCountry={billingInfo.country}
                        />
                        <ActivePackageInfo
                            activePackage={activePackage}
                            handleBuyMore={handleBuyMore}
                            submit={props.submit}
                            loading={loading}
                        />

                        <PurchasesLog purchases={purchasesRows} />

                        <PackagesModal
                            isOpen={isDialogOpen}
                            onClose={() => setDialogOpen(false)}
                            stripePromise={stripePromise}
                            addUsers={currentPurchases.some(
                                (purchase) => purchase.packageId === 'STANDARD' || purchase.packageId === 'PREMIUM'
                            )}
                            purchases={purchases}
                        />

                        <UnsavedChangesModal isOpen={blocked} onClose={reset} onConfirm={proceedNavigation} />
                    </section>
                )}
            </React.Fragment>
        );
    }
}

export function ActivePackageInfo(props) {
    const { activePackage, handleBuyMore, loading } = props;
    const packageType = activePackage ? activePackage.packageId : '';

    return (
        <div className="w-full pt-2 pb-4 pr-2 mb-4 border-t border-neutral-200">
            <div className="flex flex-col">
                <h2 className="mb-4 text-lg font-bold text-neutral-800">{_t.profile.activePackageTitle()}</h2>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row">
                {activePackage?.packageId && (
                    <div className="rounded-lg shadow-lg border w-full max-w-[250px] flex flex-col gap-4 items-start justify-between p-4">
                        <b>{_t.profile.packageTypeInfo(packageType)}</b>
                        <div className="flex flex-col text-sm md:text-md">
                            <div key="users" className="flex items-center gap-2 py-1">
                                <Icon icon="material-symbols:groups-outline" width={'1.2rem'} height={'1.2rem'} />
                                <p>{_t.package.users(packageType)}</p>
                            </div>
                            <div key="itineraries" className="flex items-center gap-2 py-1">
                                <Icon icon="bx:trip" width={'1.2rem'} height={'1.2rem'} />
                                <p>{_t.package.itineraries(packageType)}</p>
                            </div>
                            {premiumPackages.includes(packageType) && (
                                <div key="premium" className="flex items-center gap-2 py-1">
                                    <Icon icon="ic:outline-workspace-premium" width={'1.2rem'} height={'1.2rem'} />
                                    <p>{_t.package.premium(packageType)}</p>
                                </div>
                            )}
                        </div>
                        <div className="flex items-baseline justify-center gap-2 text-sm md:text-md">
                            <span className="text-sm italic">{_t.profile.expireDateInfo()}</span>
                            <span>{activePackage?.expireDate ?? ''}</span>
                        </div>
                    </div>
                )}
                <div className="rounded-lg shadow-lg border w-fit max-w-[250px] flex flex-col justify-between p-4 text-justify">
                    {packageType && (
                        <span className="text-sm italic text-neutral-600">
                            {_t.profile.buyMoreDescription(packageType)}
                        </span>
                    )}
                    <Button
                        look="primary"
                        onClick={handleBuyMore}
                        className={addClasses('mt-2')}
                        size="xl"
                        disabled={loading}
                        busy={loading}
                    >
                        <Icon
                            icon="material-symbols:payments-outline"
                            width="1.2rem"
                            height="1.2rem"
                            className="mr-2"
                        />
                        {_t.billing.buy()}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function PurchasesLog(props) {
    const { purchases } = props;
    const dateFormatter = new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    return (
        <div className="w-full pt-2 pb-4 pr-2 mb-4 border-t border-neutral-200">
            <h2 className="mb-4 text-lg font-bold text-neutral-700">{_t.billing.purchasesLogTitle()}</h2>
            <div className="border border-neutral-300 max-h-[300px] overflow-y-auto max-w-[650px] p-0 text-sm">
                <div className="grid grid-cols-[70px_1fr_1fr_1fr] content-center divide-x divide-x-neutral-300 bg-neutral-100 border-b border-neutral-300">
                    <b className="p-2 text-center">{_t.billing.statusHeading()}</b>
                    <b className="p-2">{_t.billing.packageHeading()}</b>
                    <b className="p-2">{_t.billing.purchaseDateHeading()}</b>
                    <b className="p-2">{_t.billing.expireDateHeading()}</b>
                </div>
                {purchases && purchases?.length > 0 ? (
                    purchases.map((el) => (
                        <div className="grid grid-cols-[70px_1fr_1fr_1fr] content-center divide-x divide-x-neutral-300">
                            <div className="flex items-center justify-center w-full p-2">
                                <StatusCircle status={el.status} />
                            </div>
                            <span className="p-2">{el.packageId}</span>
                            <span className="p-2">{dateFormatter.format(new Date(el.datePurchase))}</span>
                            <span className="p-2">{dateFormatter.format(new Date(el.dateExpire))}</span>
                        </div>
                    ))
                ) : (
                    <div>{_t.global.noResults()}</div>
                )}
            </div>
        </div>
    );
}

function StatusCircle(props) {
    const { status } = props;

    return (
        <FloatingTooltip content={_t.billing.statusTooltip(status)}>
            <Circle color={StatusRingColor[status]} />
        </FloatingTooltip>
    );
}

function Circle(props) {
    const { color } = props;
    const className = 'w-4 h-4 rounded-full ' + color;
    return <div className={className} />;
}
